import { bind, classNames, Component, h, tag } from "omi";
import isEmpty from "lodash-es/isEmpty";

import type { Divider, DropdownItem } from "@/wc/components/atoms/nw-dropdown/models.ts";
import { closeModal, NWIcon, NWProBadge, openProModal } from "@/wc/components";
import { Store } from "@/store";
import { tailwind } from "@/tailwind";

type Props = {
  items: DropdownItem[];
  width?: "fixed" | "fit" | "full";
  limitHeight?: boolean;
  maxHeight?: string;
  fullWidthMenu?: boolean;
  isolateMouseEvents?: boolean;
  onItemClick?: (item: DropdownItem, event?: MouseEvent) => void;
  highlightedIndex?: number;
  onMouseEnter?: (index: number) => void;
  onMouseLeave?: () => void;
};

@tag("nw-dropdown-menu")
export class NWDropdownMenu extends Component<Props> {
  static css = [tailwind];
  static propTypes = {
    items: Array,
    width: String,
    limitHeight: Boolean,
    maxHeight: String,
    fullWidthMenu: Boolean,
    isolateMouseEvents: Boolean,
    onItemClick: Function,
    highlightedIndex: Number,
    onMouseEnter: Function,
    onMouseLeave: Function,
  };

  @bind
  private handleItemClick(item: DropdownItem, event?: MouseEvent): void {
    if (event && this.props.isolateMouseEvents) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (item.type != "divider" && item.isProOption && !Store.user.isPro()) {
      closeModal();
      setTimeout(() => {
        openProModal();
      }, 350);
      return;
    }

    if (item.type === "comingSoon") {
      // Coming soon options do not close the dropdown
      item.callback(item);
      return;
    }

    // Call the parent's callback if provided
    if (this.props.onItemClick) {
      this.props.onItemClick(item, event);
    }
  }

  private isDivider(item: DropdownItem): item is Divider {
    return item.type === "divider";
  }

  render({
    items,
    width = "fixed",
    limitHeight = true,
    maxHeight,
    fullWidthMenu = false,
    isolateMouseEvents = false,
    highlightedIndex = -1,
    onMouseEnter,
    onMouseLeave,
  }: Props) {
    const itemStyles =
      "text-secondary cursor-pointer select-none text-sm px-3 h-10 flex items-center hover:bg-gray-50 space-x-2";

    return (
      <div
        class={classNames(
          "border py-1.5 flex items-center rounded-lg z-[60] bg-[#FFF] shadow-lg",
          fullWidthMenu
            ? { "w-full max-w-md": true }
            : { "w-[280px] max-w-xs": width != "fit", "w-fit max-w-xs": width === "fit" },
        )}
        onClick={e => {
          if (isolateMouseEvents) {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
      >
        <ul
          class={classNames(
            "w-full overflow-y-auto",
            {
              "max-h-60": limitHeight && isEmpty(maxHeight),
            },
            !isEmpty(maxHeight) ? [maxHeight] : {},
          )}
          tabIndex={-1}
          role="listbox"
        >
          {items.map((item, idx) => {
            if (this.isDivider(item)) return <li class="border-b bg-gray-200" />;

            if (item.shouldShow && !item.shouldShow()) {
              return null;
            }

            const content = (
              <span class="flex items-center gap-2">
                {item.icon && (
                  <div class={classNames("text-quaternary", item.iconStyle)}>
                    <NWIcon name={item.icon} width={16} height={16} />
                  </div>
                )}
                <span class="text-nowrap" unsafeHTML={{ html: item.text }}></span>
              </span>
            );

            if (item.type === "link") {
              return (
                <a
                  class={itemStyles}
                  tabIndex="0"
                  href={item.href}
                  target="_blank"
                  onClick={e => {
                    if (isolateMouseEvents) {
                      e.preventDefault();
                      e.stopPropagation();
                      window.open(item.href, "_blank");
                    }
                  }}
                >
                  {content}
                </a>
              );
            }

            if (item.type === "comingSoon") {
              return (
                <li
                  onClick={e => this.handleItemClick(item, e)}
                  tabIndex="0"
                  class={classNames(itemStyles, "justify-between !cursor-not-allowed")}
                  role="option"
                  onMouseEnter={() => onMouseEnter && onMouseEnter(idx)}
                  onMouseLeave={() => onMouseLeave && onMouseLeave()}
                >
                  <div class="flex items-center gap-2">{content}</div>
                  <span class="rounded-md border px-1 py-0.5 text-xs text-quaternary">Coming soon</span>
                </li>
              );
            }

            return (
              <li
                onClick={e => this.handleItemClick(item, e)}
                tabIndex="0"
                class={classNames(
                  itemStyles,
                  { "justify-between": item.isProOption },
                  { "bg-gray-50": idx === highlightedIndex },
                  { "justify-between": !!item.subtext },
                )}
                role="option"
                onMouseEnter={() => onMouseEnter && onMouseEnter(idx)}
                onMouseLeave={() => onMouseLeave && onMouseLeave()}
              >
                {content}
                {item.isActive && item.isActive() && <NWIcon name="tick" />}
                {item.isProOption && <NWProBadge location="dropdown-item" analyticsData={{ name: item.text }} />}
                {item.subtext && <span class="float-right text-xs text-quinary pl-2">{item.subtext}</span>}
              </li>
            );
          })}
        </ul>
      </div>
    );
  }
}
